import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { FilterChipComponent } from '../../../components/filter-chip/filter-chip.component';
import { IconComponent } from '../../../components/icon/icon.component';
import { SearchBarComponent } from '../../../components/search-bar/search-bar.component';
import { VerwendungStatus } from '../../../models/verwendung.model';
import { ASSIGNMENT_CHIPS, AssignmentAction, AssignmentRow } from '../object-assignment.util';

interface ActionChip {
  key: AssignmentAction | 'ALL';
  action: AssignmentAction | null;
  label: string;
  count: number | null;
  active: boolean;
}

/** Zeilen des Skeletts, solange Verwender oder offene Referenzen laden (L4). */
const SKELETON_ROWS = [1, 2, 3];

/**
 * Ansicht „Zuordnung zu Testfällen“ (NEX-2454, Bundle `page-objektbibliothek.jsx:174-239`): welche Testfälle und
 * Testmodule welches Objekt wie ansprechen, dazu die offenen Referenzen. Die Zeilen leitet die Detailseite aus
 * echtem TCDL ab (`object-assignment.util.ts`); die Ansicht filtert, sucht und öffnet den Besitzer.
 */
@Component({
  selector: 'app-object-assignment-view',
  imports: [TranslatePipe, RouterLink, MatButton, MatIcon, FilterChipComponent, IconComponent, SearchBarComponent],
  templateUrl: './object-assignment-view.component.html',
  styleUrl: './object-assignment-view.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ObjectAssignmentViewComponent {
  private readonly router = inject(Router);

  readonly projectId = input.required<string>();
  readonly libraryName = input.required<string>();
  readonly rows = input<AssignmentRow[]>([]);
  readonly openRows = input<AssignmentRow[]>([]);
  readonly status = input<VerwendungStatus>('laedt');
  readonly readOnly = input(true);

  readonly retry = output<void>();
  /** Der Objektname einer offenen Referenz, für den Eintrag-Dialog. */
  readonly createObject = output<string>();

  readonly search = signal('');
  readonly action = signal<AssignmentAction | null>(null);
  readonly skeletonRows = SKELETON_ROWS;

  readonly ready = computed(() => this.status() === 'bereit');

  readonly chips = computed<ActionChip[]>(() => {
    const rows = this.rows();
    const active = this.action();
    const count = (action: AssignmentAction | null): number | null =>
      this.ready() ? rows.filter((row) => action === null || row.action === action).length : null;
    return [
      { key: 'ALL', action: null, label: 'objectMappings.assignment.all', count: count(null), active: active === null },
      ...ASSIGNMENT_CHIPS.map((action) => ({
        key: action,
        action,
        label: `objectMappings.assignment.action.${action}`,
        count: count(action),
        active: active === action,
      })),
    ];
  });

  readonly shownRows = computed(() => {
    const action = this.action();
    const term = this.search().trim().toLowerCase();
    const library = this.libraryName();
    return this.rows().filter(
      (row) =>
        (action === null || row.action === action) &&
        (!term || `${row.object ?? library} ${row.ownerKey} ${row.ownerTitle}`.toLowerCase().includes(term)),
    );
  });

  readonly openTitleKey = computed(() =>
    this.openRows().length === 1 ? 'objectMappings.assignment.openOne' : 'objectMappings.assignment.openMany',
  );

  selectAction(action: AssignmentAction | null): void {
    this.action.set(action);
  }

  ownerLink(row: AssignmentRow): string[] {
    return [
      '/projects',
      this.projectId(),
      row.ownerKind === 'MODULE' ? 'test-modules' : 'test-cases',
      row.ownerId,
      'edit',
    ];
  }

  ownerStep(row: AssignmentRow): string {
    return [row.ownerKey, row.stepKey].filter(Boolean).join(' · ');
  }

  /** Klick und Enter gehen denselben Weg. */
  open(row: AssignmentRow): void {
    this.router.navigate(this.ownerLink(row));
  }
}
